/**
 * Authentication Service
 * Handles all authentication-related API calls
 */

import { SUPABASE_FUNCTIONS_URL } from '../utils/apiEndpoints';
import { supabase, isSupabaseConfigured, getSupabaseSession, SUPABASE_ANON_KEY } from './supabaseClient';

const supabaseConfigError = {
  ok: false,
  status: 500,
  data: {
    error: 'Supabase is nog niet geconfigureerd. Controleer VITE_SUPABASE_URL en VITE_SUPABASE_ANON_KEY.',
  },
};

const validatePasswordStrength = (password) => {
  if (!password || password.length < 8) {
    return 'Wachtwoord moet minimaal 8 tekens bevatten';
  }
  if (!/[A-Z]/.test(password)) {
    return 'Wachtwoord moet minimaal één hoofdletter bevatten';
  }
  if (!/[0-9]/.test(password)) {
    return 'Wachtwoord moet minimaal één cijfer bevatten';
  }
  return '';
};

const getProfileFromCurrentUserFunction = async () => {
  const { data, error } = await supabase.functions.invoke('current-user', {
    method: 'GET',
  });

  if (error) {
    throw error;
  }

  if (!data?.success || !data?.data?.gebruiker) {
    const profileError = new Error(data?.error?.message || 'Gebruiker kon niet worden opgehaald');
    profileError.status = data?.error?.code === 'ACCOUNT_NOT_CONFIRMED' ? 403 : undefined;
    throw profileError;
  }

  return data.data.gebruiker;
};

const mapSessionResponse = async (session) => {
  try {
    const gebruiker = await getProfileFromCurrentUserFunction();

    return {
      ok: true,
      status: 200,
      data: {
        message: 'Inloggen succesvol',
        accessToken: session.access_token,
        refreshToken: session.refresh_token,
        gebruiker,
      },
    };
  } catch (edgeError) {
    if (edgeError.status === 403 || edgeError.message?.includes('Bevestig eerst je e-mailadres')) {
      return {
        ok: false,
        status: 403,
        data: { error: 'Bevestig eerst je e-mailadres via de link in de e-mail.' },
      };
    }

    console.warn('Current-user Edge Function lookup failed:', edgeError.message);
    return {
      ok: false,
      status: 403,
      data: {
        error: 'Je account is nog niet gekoppeld aan een student-, werkgever- of adminprofiel.',
      },
    };
  }
};

/**
 * Register a new user
 * @param {Object} userData - User registration data
 * @returns {Promise<Object>} Registration response
 */
export const register = async (userData) => {
  if (!isSupabaseConfigured) return supabaseConfigError;

  const response = await fetch(`${SUPABASE_FUNCTIONS_URL}/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'apikey': SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify(userData),
  });

  const data = await response.json().catch(() => ({}));

  return {
    ok: response.ok && data?.success !== false,
    status: response.status,
    data: data?.success === false
      ? { error: data.error?.message || 'Registratie mislukt' }
      : (data?.data || data),
  };
};

/**
 * Login user with email and password
 * @param {string} email - User email
 * @param {string} wachtwoord - User password
 * @returns {Promise<Object>} Login response with token and user data
 */
export const login = async (email, wachtwoord) => {
  if (!isSupabaseConfigured) return supabaseConfigError;

  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password: wachtwoord,
  });

  if (error) {
    const message = error.message || '';
    if (message.toLowerCase().includes('email not confirmed') || message.toLowerCase().includes('not confirmed')) {
      return {
        ok: false,
        status: 403,
        data: { error: 'Bevestig eerst je e-mailadres via de link in de e-mail.' },
      };
    }

    return {
      ok: false,
      status: error.status || 401,
      data: { error: 'Ongeldige gegevens. Controleer je invoer.' },
    };
  }

  try {
    return await mapSessionResponse(data.session);
  } catch (profileError) {
    console.error('Supabase profile lookup failed:', profileError.message);
    return {
      ok: false,
      status: 500,
      data: { error: 'Inloggen is gelukt, maar je profiel kon niet worden geladen.' },
    };
  }
};

export const logout = async () => {
  if (supabase) {
    await supabase.auth.signOut();
  }
};

export const confirmSupabaseAccount = async () => {
  if (!isSupabaseConfigured) return supabaseConfigError;

  const session = await getSupabaseSession();
  if (!session?.access_token) {
    return {
      ok: false,
      status: 400,
      data: { error: 'De bevestigingslink is ongeldig, verlopen of al gebruikt.' },
    };
  }

  const response = await mapSessionResponse(session);
  if (!response.ok) return response;

  return {
    ...response,
    data: {
      ...response.data,
      message: 'Je account is succesvol bevestigd!',
    },
  };
};

/**
 * Request password reset email
 * @param {string} email - User email
 * @returns {Promise<Object>} Password reset request response
 */
export const forgotPassword = async (email) => {
  if (!isSupabaseConfigured) return supabaseConfigError;

  const redirectTo = `${window.location.origin}/wachtwoord-reset`;
  const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });

  if (error && error.status === 400) {
    return {
      ok: false,
      status: error.status || 400,
      data: { error: error.message || 'Reset link kon niet worden verstuurd.' },
    };
  }

  return {
    ok: true,
    status: 200,
    data: {
      message: 'Als het e-mailadres bestaat, is er een reset link verstuurd.',
    },
  };
};

/**
 * Validate password reset token
 * @param {string} token - Password reset token
 * @returns {Promise<Object>} Token validation response
 */
export const validateResetToken = async (token) => {
  if (!isSupabaseConfigured) return supabaseConfigError;

  if (token === 'url-session' || token?.includes('access_token') || token?.includes('code=')) {
    const session = await getSupabaseSession();
    if (session?.user?.email) {
      return {
        ok: true,
        status: 200,
        data: { email: session.user.email },
      };
    }
  }

  return {
    ok: false,
    status: 400,
    data: { error: 'De reset link is ongeldig, verlopen of al gebruikt.' },
  };
};

/**
 * Reset password with token
 * @param {string} token - Password reset token
 * @param {string} nieuwWachtwoord - New password
 * @param {string} bevestigWachtwoord - Password confirmation
 * @returns {Promise<Object>} Password reset response
 */
export const resetPassword = async (token, nieuwWachtwoord, bevestigWachtwoord) => {
  if (!isSupabaseConfigured) return supabaseConfigError;

  if (nieuwWachtwoord !== bevestigWachtwoord) {
    return {
      ok: false,
      status: 400,
      data: { error: 'Wachtwoorden komen niet overeen' },
    };
  }

  const passwordError = validatePasswordStrength(nieuwWachtwoord);
  if (passwordError) {
    return {
      ok: false,
      status: 400,
      data: { error: passwordError },
    };
  }

  const { error } = await supabase.auth.updateUser({ password: nieuwWachtwoord });

  if (error) {
    return {
      ok: false,
      status: error.status || 400,
      data: { error: error.message || 'Wachtwoord kon niet worden gewijzigd.' },
    };
  }

  return {
    ok: true,
    status: 200,
    data: { message: 'Je wachtwoord is succesvol gewijzigd. Je wordt doorgestuurd naar de inlogpagina.' },
  };
};

/**
 * Resend account confirmation email
 * @param {string} email - User email
 * @returns {Promise<Object>} Resend confirmation response
 */
export const resendConfirmation = async (email) => {
  if (!isSupabaseConfigured) return supabaseConfigError;

  const { error } = await supabase.auth.resend({
    type: 'signup',
    email,
    options: { emailRedirectTo: `${window.location.origin}/account-bevestigen` },
  });

  if (error) {
    return {
      ok: false,
      status: error.status || 400,
      data: { error: error.message || 'Bevestigingslink kon niet worden verstuurd.' },
    };
  }

  return {
    ok: true,
    status: 200,
    data: { message: 'Als het e-mailadres bestaat en nog niet bevestigd is, is er een nieuwe link verstuurd.' },
  };
};

export default {
  register,
  login,
  logout,
  confirmSupabaseAccount,
  forgotPassword,
  validateResetToken,
  resetPassword,
  resendConfirmation,
};
